import { useRender } from '@base-ui/react/use-render'
import { useRef, type ComponentPropsWithRef } from 'react'
import { cn } from '../../lib/cn'
import s from './DataTable.module.css'

/**
 * Tableau de liste — la **peau** des tables du back-office : ligne d'en-tête en petites
 * capitales, filets de séparation, hauteur de rangée à la densité, et le survol des seules
 * rangées qui s'ouvrent — cf. `DataTableRowAction`, plus bas.
 *
 * Un seul élément, aucun balisage imposé : le composant rend le `<table>`, l'écran écrit
 * lui-même son `<thead>`, ses `<th>`, son `<tbody>` et ses `<tr>`. Deux conséquences
 * voulues :
 *
 * - **l'accessibilité reste à l'écran** — c'est lui qui connaît ses colonnes, ses portées
 *   (`scope`) et son en-tête ; le design system ne peut pas les deviner à sa place ;
 * - **TanStack Table (D20) s'y branche sans friction** : la bibliothèque est *headless*,
 *   elle fournit les groupes d'en-têtes et les rangées et laisse l'application les rendre.
 *   Un composant qui prendrait la main sur le rendu des rangées lui barrerait la route.
 *
 * Ce qui reste donc à l'écran : les **largeurs de colonnes** et la mise en forme du
 * **contenu** des cellules. Ce qui vient d'ici : tout ce qui serait identique d'une liste
 * à l'autre.
 *
 * Une rangée qui ouvre quelque chose pose son `DataTableRowAction` dans une cellule : la
 * peau étale alors la cible sur la **rangée entière**. Celle qui n'ouvre rien ne prend ni
 * curseur ni survol — un tableau de lecture ne doit pas promettre une cible.
 *
 * ```tsx
 * <Card>
 *   <DataTable>
 *     <thead>…</thead>
 *     <tbody>…</tbody>
 *   </DataTable>
 * </Card>
 * ```
 *
 * ⚠️ La peau gouverne `text-align` des en-têtes (à gauche) par un sélecteur descendant :
 * une classe d'écran posée sur un `<th>` ne la surcharge pas — et c'est délibéré, sinon
 * l'apparence du tableau dépendrait de l'ordre d'émission des feuilles de style. Une
 * colonne alignée autrement le demande donc **à la peau**, par `data-align="end"` posé sur
 * la cellule — sur le `<th>` **comme** sur les `<td>`, en-tête et contenu allant ensemble :
 *
 * ```tsx
 * <th data-align="end">État</th>
 * …
 * <td data-align="end">…</td>
 * ```
 */
export type DataTableProps = ComponentPropsWithRef<'table'>

/**
 * Le `<table>` arrive enveloppé d'un conteneur qui **défile horizontalement**.
 *
 * Une liste de tournée porte une dizaine de colonnes, et la tablette peut être tenue en
 * portrait — le manifeste ne verrouille pas l'orientation. Sans ce conteneur, le tableau
 * élargit la page entière : la barre de titre sort de l'écran avec lui, et sur une coquille
 * bornée (`AppShell height="viewport"`) c'est le contenu voisin qui se fait rogner.
 *
 * Pas de `tabindex` sur le conteneur : les cellules portent déjà des éléments focusables —
 * la référence du contrôle est un lien — et le navigateur fait défiler le conteneur pour
 * amener l'élément focalisé à l'écran. Un arrêt de tabulation de plus n'apporterait rien et
 * s'ajouterait à chaque liste.
 */
/**
 * Les elements qui gardent leur propre geste : une case de selection, un bouton de fin de
 * rangee, un lien. Sans cette garde, cocher une case ouvrirait le controle.
 */
const OWN_GESTURE = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  // ⚠️ **Les rôles ARIA autant que les balises.** Les primitives Base UI ne rendent pas toutes
  // un `<button>` : sa case à cocher rend un élément neutre portant `role="checkbox"`, et sans
  // cette ligne cocher une demande l'ouvrait — quarante boîtes de dialogue pour attribuer un lot.
  '[role="button"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="tab"]',
  '[role="option"]',
  '[role="combobox"]',
  '[role="slider"]',
  '[contenteditable=""]',
  '[contenteditable="true"]',
].join(', ')

/**
 * Etale la cible sur la rangee entiere, **en un seul ecouteur** pose sur le tableau.
 *
 * ⚠️ C'est ce qui remplace le voile absolu du module CSS, retire le 21/09/2026 : ancre sur un
 * `<tr>`, que la specification n'oblige pas a etre un bloc conteneur, il couvrait l'ecran
 * entier et volait tous les clics de l'app.
 *
 * La commande de la rangee reste un `<button>` ou un lien reel : le clavier et les lecteurs
 * d'ecran passent par lui, la delegation n'ajoute que la surface a la souris et au doigt.
 */
function openRowFrom(target: EventTarget | null): void {
  if (!(target instanceof Element)) return
  if (target.closest(OWN_GESTURE) !== null) return

  const row = target.closest('tbody tr')

  // `null` est le cas courant : un tableau de lecture ne promet aucune cible.
  row?.querySelector<HTMLElement>('[data-row-action]')?.click()
}

export function DataTable({ className, onClick, ...props }: DataTableProps) {
  return (
    <div className={s.scroller}>
      {/*
        La regle jsx-a11y vise le `<div onClick>` sans equivalent clavier. Ici l'equivalent
        existe et c'est **lui** qui porte le geste : la commande de la rangee est un `<button>`
        ou un lien reel, focalisable et annonce. Cet ecouteur n'ajoute aucun chemin
        d'activation, il elargit la surface de celui qui existe. Poser un `role` ou un
        `tabIndex` sur le tableau pour satisfaire la regle ajouterait un arret de tabulation
        par liste et ferait annoncer le tableau comme un bouton.
      */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <table
        className={cn(s.root, className)}
        {...props}
        onClick={(event) => {
          onClick?.(event)
          openRowFrom(event.target)
        }}
      />
    </div>
  )
}

/**
 * L'action d'une rangée — ce que toucher la rangée déclenche.
 *
 * Posée dans une cellule, elle rend la **rangée entière** activable : c'est ce que fait le
 * prototype, où la rangée porte l'ouverture (`<tr data-open>` côté terrain, `tr.clickable`
 * côté bureau) et non le texte d'une cellule. Sur une tablette tenue à bout de bras, viser
 * huit caractères au stylet est le geste le plus coûteux d'une tournée.
 *
 * Le motif retenu est la **délégation** : l'action reste un `<button>` réel, et `DataTable`
 * l'active quand on touche sa rangée ailleurs. Un `onClick` posé sur le `<tr>` aurait la même
 * surface, mais ni focus, ni activation clavier, ni annonce — la rangée n'existerait qu'à la
 * souris.
 *
 * Elle ne dessine rien : la cellule garde sa typographie, la rangée porte l'affordance
 * (curseur, survol). Et comme `TextLink`, `render` l'habille sur un vrai lien le jour où
 * l'ouverture est une URL — le clic milieu et « ouvrir dans un nouvel onglet » avec :
 *
 * ```tsx
 * <tr>
 *   <td>
 *     <DataTableRowAction render={<Link to="/inspections/$id" params={{ id }} />}>
 *       {reference}
 *     </DataTableRowAction>
 *   </td>
 *   <td>{city}</td>
 * </tr>
 * ```
 *
 * ⚠️ **Une seule par rangée** : `DataTable` n'active que la première qu'il trouve, une seconde ne
 * répondrait qu'à son propre clic. Ce que la rangée porte d'autre d'interactif — une case à
 * cocher, un bouton de fin de rangée — garde son geste : un clic parti de lui n'est pas suivi.
 */
export type DataTableRowActionProps = useRender.ComponentProps<'button'>

export function DataTableRowAction({ render, className, ...props }: DataTableRowActionProps) {
  return useRender({
    render,
    defaultTagName: 'button',
    props: {
      className: cn(s.action, className),
      // La marque que `DataTable` cherche pour etaler la cible. Un attribut plutot qu'un nom
      // de classe : les modules CSS les rendent opaques, et un test les remplace par autre chose.
      'data-row-action': '',
      // Même défaut que `Button` et `TextLink` : hors d'un `render`, jamais `submit`
      // implicite — une liste vit souvent dans un formulaire de filtres.
      ...(render === undefined && { type: 'button' }),
      ...props,
    },
  })
}

/** La largeur sous laquelle une colonne ne descend pas : celle de la case de sélection. */
const MIN_COLUMN_WIDTH = 40

export interface DataTableColumnResizerProps {
  /** La largeur voulue, en pixels — l'écran la pose sur son `<th>` et la retient. */
  onResize: (width: number) => void
  /** Le double-clic : la colonne revient à la largeur que l'écran lui donne. */
  onReset: () => void
}

/**
 * La poignée de largeur d'une colonne, posée **dans** son `<th>`, sur son bord droit (choix D du
 * tableau « Pilotage GHP », 23/10/2026 : « Demandes » s'ouvre aux largeurs du prototype, et
 * chacun élargit la colonne qu'il veut).
 *
 * ⚠️ **Rien ne se dessine au repos** : le prototype n'a pas de poignée, c'est le curseur qui la
 * dit. Ce que la peau ne fait pas, c'est **retenir** la largeur — l'écran la pose sur son `<th>`
 * et décide où elle survit.
 *
 * ⚠️ **À la souris seulement, et cachée aux lecteurs d'écran.** Annoncée, elle entrerait dans le
 * nom de l'en-tête (« Client Largeur de la colonne… »), répété à chaque cellule lue. Ce qu'elle
 * retire au clavier est un confort tant que le tableau reste en disposition automatique et
 * qu'aucune cellule ne rogne : chaque colonne y tient son contenu. Elle n'est pas un arrêt de
 * tabulation, et ce que porte l'en-tête (tri, case) reste au clavier.
 *
 * La largeur de départ se lit sur l'en-tête au moment du geste, pas dans une prop : la colonne
 * n'a de largeur connue qu'une fois le tableau mis en page.
 */
export function DataTableColumnResizer({ onResize, onReset }: DataTableColumnResizerProps) {
  const drag = useRef<{ x: number; width: number } | null>(null)

  return (
    <span
      aria-hidden
      className={s.resizer}
      onPointerDown={(event) => {
        if (event.button !== 0) return
        // Ni sélection de texte, ni glisser natif pendant le geste.
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        const header = event.currentTarget.parentElement
        drag.current = { x: event.clientX, width: header?.getBoundingClientRect().width ?? 0 }
      }}
      onPointerMove={(event) => {
        if (drag.current === null) return
        const width = drag.current.width + event.clientX - drag.current.x
        onResize(Math.max(MIN_COLUMN_WIDTH, Math.round(width)))
      }}
      onPointerUp={() => {
        drag.current = null
      }}
      onPointerCancel={() => {
        drag.current = null
      }}
      onDoubleClick={onReset}
    />
  )
}
